import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchAllTournaments, type TournamentProjectionItem } from "../utils/homeApi";
import { cleanTournamentName } from "../utils/cleanTournamentName";

const PAGE_SIZE = 50;

type ViewMode = "all" | "singles" | "teams";

function tournamentTypeLabel(t: TournamentProjectionItem): "Singiel" | "Debel" | "Mieszany" {
  const hasSingles = (t.singlesMatchCount ?? 0) > 0;
  const hasDoubles = (t.doublesMatchCount ?? 0) > 0;
  if (hasSingles && hasDoubles) {
    return "Mieszany";
  }

  if (hasDoubles) {
    return "Debel";
  }

  return "Singiel";
}

const TournamentResults: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [allTournaments, setAllTournaments] = useState<TournamentProjectionItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const viewParam = searchParams.get("view");
    if (viewParam === "all" || viewParam === "singles" || viewParam === "teams") {
      setViewMode(viewParam);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchTournaments = async () => {
      try {
        setLoading(true);
        const res = await fetchAllTournaments({ limit: 500 });
        let acc: TournamentProjectionItem[] = res.items;
        let offset = res.items.length;
        while (offset < res.total) {
          const next = await fetchAllTournaments({ limit: 500, offset });
          acc = acc.concat(next.items);
          offset += next.items.length;
          if (next.items.length === 0) {
            break;
          }
        }
        // Chronological desc — najnowsze pierwsze. Date pierwszy, fallback
        // tournamentNumber dla wpisów bez daty.
        acc.sort((a, b) => {
          const da = a.date ? new Date(a.date).getTime() : 0;
          const db = b.date ? new Date(b.date).getTime() : 0;
          if (db !== da) {
            return db - da;
          }
          return b.tournamentNumber - a.tournamentNumber;
        });
        setAllTournaments(acc);
      } catch (error) {
        console.error("Error loading tournaments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTournaments();
  }, []);

  const tournaments = allTournaments.filter((t) => {
    if (viewMode === "singles") {
      return (t.singlesMatchCount ?? 0) > 0;
    }

    if (viewMode === "teams") {
      return (t.doublesMatchCount ?? 0) > 0;
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(tournaments.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pageStart = currentPage * PAGE_SIZE;
  const visibleTournaments = tournaments.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    setPage(0);
  }, [viewMode]);

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <div>Wczytywanie turniejów…</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>Ostatnie wyniki</h2>
        <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
          <button
            onClick={() => setViewMode("all")}
            className={`btn-secondary ${viewMode === "all" ? "active" : ""}`}
          >
            Wszystkie wyniki
          </button>
          <button
            onClick={() => setViewMode("singles")}
            className={`btn-secondary ${viewMode === "singles" ? "active" : ""}`}
          >
            Singiel
          </button>
          <button
            onClick={() => setViewMode("teams")}
            className={`btn-secondary ${viewMode === "teams" ? "active" : ""}`}
          >
            Debel
          </button>
        </div>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>Data</th>
                <th>Zawodnicy</th>
                <th>Liczba meczów</th>
                {viewMode === "all" && <th>Typ</th>}
                <th>Szczegóły</th>
              </tr>
            </thead>
            <tbody>
              {visibleTournaments.map((tournament) => {
                const type = tournamentTypeLabel(tournament);
                const matchCount =
                  viewMode === "singles"
                    ? tournament.singlesMatchCount
                    : viewMode === "teams"
                    ? tournament.doublesMatchCount
                    : tournament.matchCount;
                // Kolejka ligi → /leagues/:leagueId/rounds/:roundId
                // (rich tournament view rendered inline na tej stronie).
                // Standalone turniej → /tournament/:n.
                const isLeagueRound = !!(tournament.leagueId && tournament.leagueRoundId);
                const targetPath = isLeagueRound
                  ? `/leagues/${tournament.leagueId}/rounds/${tournament.leagueRoundId}`
                  : `/tournament/${tournament.tournamentNumber}`;
                const linkLabel = "Zobacz szczegóły";
                const dateLabel = tournament.date
                  ? new Date(tournament.date).toLocaleDateString("pl-PL", {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    })
                  : "—";
                return (
                  <tr
                    key={tournament.tournamentNumber}
                    onClick={() => navigate(targetPath)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                        <strong style={{ lineHeight: 1.3 }}>
                          {tournament.name
                            ? cleanTournamentName(tournament.name)
                            : `Wydarzenie #${tournament.tournamentNumber}`}
                        </strong>
                        {isLeagueRound && (
                          <span
                            style={{
                              alignSelf: "flex-start",
                              padding: "0.1rem 0.5rem",
                              borderRadius: "12px",
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              background: "#e2e8f0",
                              color: "#2d3748",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Kolejka ligi
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap", color: "#666" }}>{dateLabel}</td>
                    <td>{tournament.uniquePlayerCount}</td>
                    <td>{matchCount}</td>
                    {viewMode === "all" && (
                      <td>
                        <span
                          style={{
                            padding: "0.25rem 0.5rem",
                            borderRadius: "15px",
                            background:
                              type === "Mieszany"
                                ? "#9f7aea"
                                : type === "Singiel"
                                ? "#4299e1"
                                : "#68d391",
                            color: "white",
                            fontSize: "0.8rem",
                            fontWeight: "600",
                          }}
                        >
                          {type}
                        </span>
                      </td>
                    )}
                    <td>
                      <Link to={targetPath} className="player-link">
                        {linkLabel}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="pagination" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '1rem' }}>
            <button
              className="btn-secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              ← Poprzednia
            </button>
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              Strona {currentPage + 1} z {totalPages} · {tournaments.length}
            </span>
            <button
              className="btn-secondary"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              Następna →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentResults;
